/* cspell:ignore nics */
import { buildNicSummaryLines } from '@/components-biz/NicConfiguration';
import { hasMeaningfulSharedMemoryConfig, sharedMemoryFromSystemDevices } from '@/delivery-type-plugin/capabilities/shared-memory';

import { BASE_INFO_OPTION_META_KEYS } from '../../../capabilities/base-info/fieldKeys';
import { readConfiguredCpuFromRecord } from '../../../capabilities/compute';
import { filterConfiguredContainerVolumes, listPricedClaimTemplateVolumes } from '../../../capabilities/container-storage/utils';
import { NETWORK_OPTION_META_KEYS } from '../../../capabilities/network/fieldKeys';
import { serializeServiceListForApi } from '../../../capabilities/service/serviceApiField';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { resolveOptionDisplayValue } from '../../../platform/option-meta';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import { resolvePricingRequirement, stripEmptyObjects } from '../../../utils';
import { pickPricingResourceMeta } from '../../../utils/pricingResourceMeta';
import { resolveSubmitOverlayText } from '../../_shared/common/submit/submitValidation';
import { ciBandwidth, ciCpu, ciDataStorage, ciGpu, ciMemory, ciSystemStorage } from '../resourceConfigs';
import { filterCiSpecByEnabledModules, isCiModuleEnabled } from './enabledModules';
import type { CiApiPayload, CiSpec } from './schema';
import { serializeContainerDataVolumes, serializeContainerGpu, serializeContainerSystemVolume } from './volumes';

function previewRow(id: string, group: string, label: string, value: unknown): DisplayDescriptor {
  const text = value === undefined || value === null || String(value).trim() === '' ? undefined : String(value);
  return { id, group, label, value: text };
}

function formatMemory(memory: CiSpec['resource']['memory']): string | undefined {
  if (memory == null || memory === '') return undefined;
  if (typeof memory === 'object' && 'request' in memory) {
    const req = memory.request;
    return req != null ? `${String(req).replace(/Gi$/i, '')} Gi` : undefined;
  }
  return `${String(memory).replace(/Gi$/i, '')} Gi`;
}

function resolveOverlayText(...candidates: unknown[]): string {
  return resolveSubmitOverlayText(...candidates);
}

export function serializeContainerApiBody(spec: CiSpec, ctx: PipelineContext): CiApiPayload {
  const name = resolveOverlayText(ctx.overlay?.name, ctx.overlay?.alias, spec.name);
  const workspace = resolveOverlayText(ctx.overlay?.workspace, spec.workspace);
  const vpcName = spec.resource.network.vpc?.name?.trim() ?? '';
  const creator = ctx.overlay?.creator ?? spec.creator ?? '';

  const services = spec.resource.network.services ?? spec.resource.network.service ?? [];
  const network = {
    ...(vpcName ? { vpc: vpcName } : {}),
    subnet: spec.resource.network.subnet?.name ?? 'default',
    securitygroup: spec.resource.network.securitygroup?.name,
    services: serializeServiceListForApi(services) ?? [],
    dnsConfig: spec.resource.network.dnsConfig,
    ...(spec.resource.network.nics?.length ? { nics: spec.resource.network.nics } : {}),
  } as Record<string, unknown>;

  const bandwidth = spec.resource.network.Bandwidth ?? spec.resource.network.egressRate;
  if (bandwidth != null && String(bandwidth).trim() !== '') {
    network.egressRate = String(bandwidth);
  }

  const cpuRequest = readConfiguredCpuFromRecord(spec.resource.cpu);
  const memoryGi =
    typeof spec.resource.memory === 'number' ? spec.resource.memory : Number(String(spec.resource.memory ?? '').replace(/Gi$/i, ''));
  const gpu = serializeContainerGpu(spec.resource.gpu as Record<string, unknown> | undefined);
  const systemVolume = serializeContainerSystemVolume(spec.systemDiskSize);
  const volumes = [
    ...(systemVolume ? [systemVolume] : []),
    ...serializeContainerDataVolumes(filterConfiguredContainerVolumes(spec.volumes)),
  ];

  const payload: CiApiPayload = {
    name,
    description: resolveOverlayText(ctx.overlay?.description, spec.description) || undefined,
    runStrategy: spec.runStrategy || 'Always',
    workspace: workspace ? [workspace] : [],
    creator,
    system: {
      ...spec.system,
      url: spec.system?.url ?? '',
      architecture: spec.system?.architecture,
      command: spec.system?.command ?? [],
      args: spec.system?.args ?? [],
      env: spec.system?.env ?? [],
      ...(spec.system?.options ? { options: spec.system.options } : {}),
    },
    resource: stripEmptyObjects({
      ...(cpuRequest ? { cpu: { request: cpuRequest, limit: cpuRequest } } : {}),
      ...(Number.isFinite(memoryGi) && memoryGi > 0 ? { memory: { request: `${memoryGi}Gi`, limit: `${memoryGi}Gi` } } : {}),
      ...(gpu ? { gpu } : {}),
      network,
      volume: volumes,
    }),
    architecture: spec.system?.architecture,
  };

  const datacenter = spec.datacenter ?? ctx.cluster ?? ctx.overlay?.datacenter;
  if (datacenter) payload.datacenter = datacenter;
  if (spec.system?.registry && (spec.system.registry.server || spec.system.registry.username || spec.system.registry.password)) {
    payload.system = { ...(payload.system as Record<string, unknown>), registry: spec.system.registry };
  }

  return stripEmptyObjects(payload) as CiApiPayload;
}

function readCiCpuTotal(spec: CiSpec): number | undefined {
  return readConfiguredCpuFromRecord(spec.resource.cpu);
}

function readCiMemoryGi(memory: CiSpec['resource']['memory']): number | undefined {
  if (memory == null || memory === '') return undefined;
  if (typeof memory === 'object' && 'request' in memory) {
    const req = memory.request;
    if (req == null) return undefined;
    const num = Number(String(req).replace(/Gi$/i, ''));
    return Number.isFinite(num) && num > 0 ? num : undefined;
  }
  const num = Number(String(memory).replace(/Gi$/i, ''));
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function hasConfiguredCiGpu(gpu: CiSpec['resource']['gpu']): boolean {
  if (!gpu) return false;
  return Boolean(
    (gpu.number != null && String(gpu.number).trim() !== '') ||
      (gpu.vendor != null && String(gpu.vendor).trim() !== '') ||
      (gpu.device != null && String(gpu.device).trim() !== ''),
  );
}

export function pickCiPresetFields(spec: CiSpec, ctx: PipelineContext): unknown {
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const filtered = filterCiSpecByEnabledModules(spec, enabledModules, skuType);
  return {
    name: filtered.name,
    description: filtered.description,
    runStrategy: filtered.runStrategy,
    workspace: filtered.workspace,
    system: filtered.system,
    systemDiskSize: filtered.systemDiskSize,
    ...(filtered.systemDiskPricing ? { systemDiskPricing: filtered.systemDiskPricing } : {}),
    resource: filtered.resource,
    volumes: filterConfiguredContainerVolumes(filtered.volumes),
    ...(filtered.optionMeta ? { optionMeta: filtered.optionMeta } : {}),
    ...(enabledModules ? { skuResourceModules: enabledModules } : {}),
  };
}

export function buildContainerDisplayDescriptors(spec: CiSpec, ctx: PipelineContext): DisplayDescriptor[] {
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const filtered = filterCiSpecByEnabledModules(spec, enabledModules, skuType);
  const optionMetaForm = spec.optionMeta ? { optionMeta: spec.optionMeta } : {};
  const network = filtered.resource.network;
  const cpuTotal = readCiCpuTotal(filtered);
  const bandwidth = network.Bandwidth ?? network.egressRate;

  const baseInfo: DisplayDescriptor[] = isCiModuleEnabled('baseInfo', enabledModules, skuType)
    ? [
        previewRow('name', 'baseInfo', '名称', filtered.name),
        previewRow('description', 'baseInfo', '描述', filtered.description),
        previewRow(
          'workspace',
          'baseInfo',
          '租户空间',
          resolveOptionDisplayValue(optionMetaForm, 'workspace', filtered.workspace, BASE_INFO_OPTION_META_KEYS.workspace),
        ),
        previewRow('namespace', 'baseInfo', '命名空间', filtered.namespace ?? ctx.namespace),
      ]
    : [];

  const networkItems: DisplayDescriptor[] = isCiModuleEnabled('network', enabledModules, skuType)
    ? [
        previewRow(
          'vpc',
          'network',
          'VPC',
          resolveOptionDisplayValue(optionMetaForm, 'resource.network.vpc', network.vpc?.name, NETWORK_OPTION_META_KEYS.vpc),
        ),
        previewRow(
          'securitygroup',
          'network',
          '安全组',
          resolveOptionDisplayValue(
            optionMetaForm,
            'resource.network.securitygroup',
            network.securitygroup?.name,
            NETWORK_OPTION_META_KEYS.securitygroup,
          ),
        ),
      ]
    : [];

  const systemSetting: DisplayDescriptor[] = isCiModuleEnabled('systemSetting', enabledModules, skuType)
    ? [
        previewRow('runStrategy', 'systemSetting', '运行策略', filtered.runStrategy),
        previewRow('image', 'systemSetting', '镜像', filtered.system?.name ?? filtered.system?.image ?? filtered.system?.url),
        previewRow('systemDisk', 'systemSetting', '系统盘', filtered.systemDiskSize ? `${filtered.systemDiskSize} Gi` : undefined),
      ]
    : [];

  const startupCommandArgsItems: DisplayDescriptor[] = isCiModuleEnabled('startupCommandArgs', enabledModules, skuType)
    ? [
        previewRow('command', 'startupCommandArgs', '启动命令', filtered.system?.command?.join(' ')),
        previewRow('args', 'startupCommandArgs', '启动参数', filtered.system?.args?.join(' ')),
      ]
    : [];

  const envVarsItems: DisplayDescriptor[] = isCiModuleEnabled('envVars', enabledModules, skuType)
    ? (filtered.system?.env ?? []).map((item, index) => previewRow(`env-${index}`, 'envVars', item.name, item.value))
    : [];

  const sharedMemory = sharedMemoryFromSystemDevices(filtered.system?.devices as Record<string, unknown> | undefined);
  const sharedMemorySize = hasMeaningfulSharedMemoryConfig(sharedMemory) ? sharedMemory?.sizeLimit : undefined;
  const nicLines = buildNicSummaryLines(filtered.resource.network.nics);

  const resourceItems: DisplayDescriptor[] = isCiModuleEnabled('resource', enabledModules, skuType)
    ? [
        previewRow('architecture', 'resource', '架构', filtered.system?.architecture),
        previewRow('cpu', 'resource', 'CPU', cpuTotal ? `${cpuTotal} 核` : undefined),
        previewRow('memory', 'resource', '内存', formatMemory(filtered.resource.memory)),
        ...(sharedMemorySize ? [previewRow('sharedMemory', 'resource', '共享内存', sharedMemorySize)] : []),
        previewRow(
          'gpu',
          'resource',
          'GPU',
          hasConfiguredCiGpu(filtered.resource.gpu) ? String(filtered.resource.gpu?.number ?? 1) : undefined,
        ),
        ...(nicLines.length ? [previewRow('nic', 'resource', '网卡', nicLines.join('; '))] : []),
        previewRow('bandwidth', 'resource', '网络带宽', bandwidth),
      ]
    : [];

  const storageItems: DisplayDescriptor[] = isCiModuleEnabled('storage', enabledModules, skuType)
    ? [previewRow('sharedVolume', 'storage', '存储配置', filtered.volumes?.length ? `${filtered.volumes.length} 个` : undefined)]
    : [];

  const dnsItems: DisplayDescriptor[] = isCiModuleEnabled('dns', enabledModules, skuType)
    ? [
        previewRow('dnsNameservers', 'dns', 'DNS 服务器', network.dnsConfig?.nameservers?.join('、')),
        previewRow('dnsSearches', 'dns', 'DNS 搜索域', network.dnsConfig?.searches?.join('、')),
      ]
    : [];

  return [
    ...baseInfo,
    ...networkItems,
    ...systemSetting,
    ...startupCommandArgsItems,
    ...envVarsItems,
    ...resourceItems,
    ...storageItems,
    ...dnsItems,
  ];
}

export function buildCiResourceConfigs(spec: CiSpec, ctx: PipelineContext): ResourceType[] {
  const filtered = filterCiSpecByEnabledModules(spec, ctx.sku?.enabledModules, ctx.sku?.skuType);
  const configs: ResourceType[] = [];
  const paymentTiming = ctx.paymentTiming;
  const purpose = ctx.resourceQuantityPurpose;
  const skuType = ctx.sku?.skuType;
  const cpuTotal = readCiCpuTotal(filtered);
  const cpuPricing = filtered.resource.cpu?.pricing ?? filtered.resource.cpuPricing;
  const cpuRequirement = resolvePricingRequirement(cpuTotal, { paymentTiming, skuType, vendorMeta: cpuPricing, purpose });
  if (cpuRequirement != null) {
    configs.push({
      ...ciCpu,
      ...pickPricingResourceMeta(cpuPricing, cpuRequirement),
      requirement: cpuRequirement,
    });
  }

  const memoryGi = readCiMemoryGi(filtered.resource.memory);
  const memoryPricing = filtered.resource.memoryPricing;
  const memoryRequirement = resolvePricingRequirement(memoryGi, { paymentTiming, skuType, vendorMeta: memoryPricing, purpose });
  if (memoryRequirement != null) {
    configs.push({
      ...ciMemory,
      ...pickPricingResourceMeta(memoryPricing, memoryRequirement),
      requirement: memoryRequirement,
    });
  }

  const gpu = filtered.resource.gpu;
  const gpuPricing = filtered.resource.gpuPricing;
  /** GPU 厂商设备写在 resource.gpu（含 quantityUnit），gpuPricing 为兼容残留 */
  const gpuPricingMeta = {
    ...gpuPricing,
    vendor: gpu?.vendor ?? gpuPricing?.vendor,
    device: gpu?.device ?? gpuPricing?.device,
    vendorID: gpu?.vendorID ?? gpuPricing?.vendorID,
    deviceID: gpu?.deviceID ?? gpuPricing?.deviceID,
    quantityUnit:
      (gpu as { quantityUnit?: string } | undefined)?.quantityUnit ?? (gpuPricing as { quantityUnit?: string } | undefined)?.quantityUnit,
  };
  const gpuRequirement = resolvePricingRequirement(gpu?.number, { paymentTiming, skuType, vendorMeta: gpuPricingMeta, purpose });
  if (gpuRequirement != null) {
    configs.push({
      ...ciGpu,
      ...pickPricingResourceMeta(gpuPricingMeta, gpuRequirement),
      requirement: gpuRequirement,
    });
  }

  const bandwidth = filtered.resource.network.Bandwidth ?? filtered.resource.network.egressRate;
  const bandwidthPricing = filtered.resource.network.bandwidthPricing;
  const bandwidthRequirement = resolvePricingRequirement(bandwidth, { paymentTiming, skuType, vendorMeta: bandwidthPricing, purpose });
  if (bandwidthRequirement != null) {
    configs.push({
      ...ciBandwidth,
      ...pickPricingResourceMeta(bandwidthPricing, bandwidthRequirement),
      requirement: bandwidthRequirement,
    });
  }

  const systemDiskPricing = filtered.systemDiskPricing;
  const systemDiskRequirement = resolvePricingRequirement(filtered.systemDiskSize, {
    paymentTiming,
    skuType,
    vendorMeta: systemDiskPricing,
    purpose,
  });
  if (systemDiskRequirement != null) {
    configs.push({
      ...ciSystemStorage,
      ...pickPricingResourceMeta(systemDiskPricing, systemDiskRequirement),
      requirement: systemDiskRequirement,
    });
  }

  let pricedClaimTemplateIndex = 0;
  listPricedClaimTemplateVolumes(filtered.volumes, { paymentTiming }).forEach((item) => {
    const requirement = resolvePricingRequirement(item.volumeCapacity, { paymentTiming, skuType, vendorMeta: item.pricing, purpose });
    if (requirement == null) return;
    configs.push({
      ...ciDataStorage,
      description: pricedClaimTemplateIndex > 0 ? `${ciDataStorage.description}${pricedClaimTemplateIndex + 1}` : ciDataStorage.description,
      ...pickPricingResourceMeta(item.pricing, requirement),
      requirement,
    });
    pricedClaimTemplateIndex += 1;
  });

  return configs;
}
